import { useFilesByInsuredQuery } from "@qw/graphql";
import { useAtom } from "jotai";
import { type FC, type PropsWithChildren, useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router";
import { correspondenceAtom, deletedFilesAtom, fileLabelsAtom, foldersVisibleAtom, selectTagsAtom } from "../atoms";
import { type InsuredFile, InsuredFilesContext } from "../hooks/use-insured-files";
import { buildTagTree } from "../util/build-tag-tree";

export interface InsuredFilesProviderProps extends PropsWithChildren {
  id?: string | null;
}

export const InsuredFilesProvider: FC<InsuredFilesProviderProps> = ({ id, ...props }) => {
  const { fileId, insuredId: insuredIdParam } = useParams();
  const insuredId = id || insuredIdParam;
  const [correspondence, setCorrespondence] = useAtom(correspondenceAtom);
  const [deletedFiles, setDeletedFiles] = useAtom(deletedFilesAtom);
  const [fileLabels, setFileLabels] = useAtom(fileLabelsAtom);
  const [foldersVisible, setFoldersVisible] = useAtom(foldersVisibleAtom);
  const [selectedFileId, setSelectedFileId] = useState<string | undefined>();
  const [selectedTags, setSelectedTags] = useAtom(selectTagsAtom);
  const [query, setQuery] = useState("");

  // Reset file, folder and search state when insuredId changes to prevent showing stale state from previous insured
  useEffect(() => {
    setSelectedFileId(undefined);
    setSelectedTags("");
    setFoldersVisible(false);
    setQuery("");
  }, [insuredId, setFoldersVisible, setSelectedTags]);

  const { data: { filesByInsured = [] } = {}, loading } = useFilesByInsuredQuery({
    variables: {
      input: {
        insuredId: insuredId || "",
        includeCorrespondence: correspondence,
        deletedAt: deletedFiles,
      },
    },
    skip: !insuredId,
    onCompleted: () => fileId && setSelectedFileId(fileId),
  });

  // Derive selectedFile from filesByInsured so it always has fresh data after refetches
  const selectedFile = useMemo(
    () => filesByInsured.find((file) => file.id === selectedFileId),
    [filesByInsured, selectedFileId]
  );

  // Wrapper to accept file object but store only the ID
  const setSelectedFile = useCallback((file: InsuredFile | undefined) => {
    setSelectedFileId(file?.id);
  }, []);

  const tagTree = useMemo(() => buildTagTree(filesByInsured), [filesByInsured]);

  const value = useMemo(
    () => ({
      correspondence,
      deletedFiles,
      fileId,
      fileLabels,
      files: filesByInsured,
      foldersVisible,
      insuredId: insuredId as string, // Safe: early return below handles undefined case
      loading,
      query,
      selectedFile,
      selectedTags,
      tagTree,
      setCorrespondence,
      setDeletedFiles,
      setFileLabels,
      setFoldersVisible,
      setQuery,
      setSelectedFile,
      setSelectedTags,
    }),
    [
      correspondence,
      deletedFiles,
      fileId,
      fileLabels,
      filesByInsured,
      foldersVisible,
      insuredId,
      loading,
      query,
      selectedFile,
      selectedTags,
      tagTree,
      setCorrespondence,
      setDeletedFiles,
      setFileLabels,
      setFoldersVisible,
      setSelectedFile,
      setSelectedTags,
    ]
  );

  if (!insuredId) {
    return <div>No Insured Id found.</div>;
  }

  return <InsuredFilesContext.Provider value={value} {...props} />;
};
